import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle, AlertCircle, Info } from "lucide-react";

export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onClose,
  isDangerous = false,
  isLoading = false,
  darkMode = false,
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000000,
            padding: "1rem",
            backdropFilter: "blur(4px)",
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: darkMode ? "#1e293b" : "#ffffff",
              borderRadius: "1rem",
              padding: "2rem",
              maxWidth: "400px",
              width: "100%",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
              position: "relative",
            }}
          >
            <button
              onClick={onClose}
              disabled={isLoading}
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                background: "transparent",
                border: "none",
                cursor: isLoading ? "not-allowed" : "pointer",
                color: darkMode ? "#94a3b8" : "#64748b",
                padding: "0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "0.5rem",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = darkMode
                  ? "#334155"
                  : "#f1f5f9")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <X size={20} />
            </button>

            <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: isDangerous
                    ? darkMode
                      ? "rgba(239, 68, 68, 0.1)"
                      : "rgba(239, 68, 68, 0.1)"
                    : darkMode
                      ? "rgba(99, 102, 241, 0.1)"
                      : "rgba(79, 70, 229, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1rem",
                  color: isDangerous
                    ? "#ef4444"
                    : darkMode
                      ? "#818cf8"
                      : "#4f46e5",
                }}
              >
                {isDangerous ? (
                  <AlertTriangle size={24} />
                ) : (
                  <AlertCircle size={24} />
                )}
              </div>
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  color: darkMode ? "#f8fafc" : "#0f172a",
                  marginBottom: "0.75rem",
                }}
              >
                {title}
              </h3>
              <p
                style={{
                  fontSize: "0.95rem",
                  color: darkMode ? "#94a3b8" : "#64748b",
                  lineHeight: "1.5",
                }}
              >
                {message}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "0.75rem",
                marginTop: "2rem",
              }}
            >
              <button
                onClick={onClose}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  backgroundColor: "transparent",
                  color: darkMode ? "#cbd5e1" : "#475569",
                  borderRadius: "0.5rem",
                  border: `1px solid ${darkMode ? "#475569" : "#cbd5e1"}`,
                  fontWeight: "500",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  !isLoading &&
                  (e.currentTarget.style.backgroundColor = darkMode
                    ? "#334155"
                    : "#f8fafc")
                }
                onMouseLeave={(e) =>
                  !isLoading &&
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  backgroundColor: isDangerous ? "#ef4444" : "#4f46e5",
                  color: "white",
                  borderRadius: "0.5rem",
                  border: "none",
                  fontWeight: "500",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "background-color 0.2s, opacity 0.2s",
                  opacity: isLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) =>
                  !isLoading &&
                  (e.currentTarget.style.backgroundColor = isDangerous
                    ? "#dc2626"
                    : "#4338ca")
                }
                onMouseLeave={(e) =>
                  !isLoading &&
                  (e.currentTarget.style.backgroundColor = isDangerous
                    ? "#ef4444"
                    : "#4f46e5")
                }
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-small"
                      style={{
                        width: 16,
                        height: 16,
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "white",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Processing...
                  </>
                ) : (
                  confirmText
                )}
              </button>
            </div>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
